import { accentAlpha, landingPalette } from '@/components/Landing/landingTokens';
import React, { useState, useCallback, useEffect } from 'react';
import { View, Pressable, Linking } from 'react-native';
import { usePathname } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';

export const DocsFeedback = () => {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const pathname = usePathname();

  const [feedback, setFeedback] = useState(null); // null | 'up' | 'down'

  // Reset the widget when navigating to a different page
  useEffect(() => {
    setFeedback(null);
  }, [pathname]);

  const handleFeedback = useCallback(
    (type) => {
      setFeedback(type);
      // Could be wired to an analytics endpoint in the future
      // e.g. trackEvent('docs_feedback', { page: pathname, type });
    },
    [pathname],
  );

  const handleContact = useCallback(() => {
    Linking.openURL('mailto:support@gab.ai?subject=Docs Feedback');
  }, []);

  return (
    <View
      style={{
        marginTop: 40,
        paddingTop: 24,
        borderTopWidth: 1,
        borderTopColor: isDark
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.06)',
        alignItems: 'center',
      }}
    >
      {feedback === null ? (
        <View style={{ alignItems: 'center' }}>
          <Text
            tag="p"
            size="m"
            color="secondary"
            style={{ marginBottom: 12 }}
          >
            Was this page helpful?
          </Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <Pressable
              onPress={() => handleFeedback('up')}
              accessibilityRole="button"
              accessibilityLabel="Yes, this page was helpful"
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 10,
                backgroundColor: hovered
                  ? accentAlpha(isDark ? 0.15 : 0.1, isDark)
                  : isDark
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(0,0,0,0.02)',
                borderWidth: 1,
                borderColor: hovered
                  ? accentAlpha(0.3, isDark)
                  : isDark
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
              })}
            >
              <Icon name="thumbs-up" size={14} color={landingPalette(isDark).accent} />
              <Text tag="span" size="m" weight="medium">
                Yes
              </Text>
            </Pressable>

            <Pressable
              onPress={() => handleFeedback('down')}
              accessibilityRole="button"
              accessibilityLabel="No, this page was not helpful"
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 10,
                backgroundColor: hovered
                  ? isDark
                    ? 'rgba(239,68,68,0.12)'
                    : 'rgba(239,68,68,0.08)'
                  : isDark
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(0,0,0,0.02)',
                borderWidth: 1,
                borderColor: hovered
                  ? 'rgba(239,68,68,0.3)'
                  : isDark
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
              })}
            >
              <Icon name="thumbs-down" size={14} color="#EF4444" />
              <Text tag="span" size="m" weight="medium">
                No
              </Text>
            </Pressable>
          </View>
        </View>
      ) : (
        <View style={{ alignItems: 'center' }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <Icon
              name={feedback === 'up' ? 'face-smile' : 'face-frown'}
              size={16}
              color={feedback === 'up' ? landingPalette(isDark).accent : '#F59E0B'}
            />
            <Text tag="p" size="m" weight="medium">
              {feedback === 'up'
                ? 'Thanks for the feedback!'
                : 'Sorry to hear that.'}
            </Text>
          </View>
          {feedback === 'down' && (
            <Pressable
              onPress={handleContact}
              style={({ hovered }) => ({
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: hovered
                  ? isDark
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)'
                  : 'transparent',
              })}
            >
              <Text tag="span" size="n" color="secondary" style={{ textDecorationLine: 'underline' }}>
                Let us know how we can improve
              </Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

export default DocsFeedback;
